import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Tab,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { TabList, TabContext, TabPanel } from '@mui/lab';
import { useUserItem } from './hooks';
import { History } from 'components/History/';
import { General } from './General';
import { Licenses } from './Licenses';
import { t } from 'i18next';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { DeactivateUserModal, ResetPasswordModal } from './components';
import { getStatusColor, StatusLabels } from '@util/user';
import { formatDate } from '@util/date.ts';

interface IUserItemProps {
  open: boolean;
  handleClose: () => void;
  itemId: string;
}

export const UserItem = ({ open, handleClose, itemId }: IUserItemProps) => {
  const {
    handleTriggerChangeTab,
    tabIndex,
    user,
    userHistory,
    isResetPasswordOpen,
    handleResetPasswordOpen,
    handleResetPasswordClose,
    confirmResetPassword,
    isResendInvitationOpen,
    handleResendInvitationOpen,
    handleResendInvitationClose,
    confirmResendInvitation,
    isDeactivateOpen,
    handleDeactivateOpen,
    handleDeactivateClose,
    confirmDeactivate,
    historyPage,
    setHistoryPage,
    handleCloseTrigger,
    isUserDeactivated,
    reactivateUser,
    isMutationPending,
    handleConfirmDeactivateOpen,
    handleConfirmDeactivateClose,
    isConfirmDeactivateOpen,
    historyTabIndex,
  } = useUserItem({
    id: itemId,
    handleClose,
  });

  const userLastActive = user.last_active ? formatDate(user.last_active) : '';
  const isResendInviteDisabled =
    user.status === 'active' ||
    user.status === 'deactivated' ||
    user.status === 'password_reset' ||
    isMutationPending;
  const isResetPasswordDisabled =
    user.status === 'deactivated' ||
    user.status === 'invitation_pending' ||
    user.status === 'invitation_expired';
  isMutationPending;
  const isDeactivateUserDisabled = isMutationPending;

  return (
    <Drawer
      ModalProps={{ onClose: handleCloseTrigger }}
      anchor='right'
      open={open}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '600px',
          height: '100%',
          paddingX: 3,
          paddingY: 3,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
        }}
      >
        {Object.keys(user).length > 1 ? (
          <>
            <Box
              sx={{
                display: 'flex',
                position: 'relative',
                justifyContent: 'space-between',
                width: '100%',
                padding: '0',
                pb: '1rem',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <AssignmentIndIcon
                  sx={{ width: '36px', height: '36px' }}
                  color='primary'
                />

                <Box
                  sx={{ display: 'flex', flexDirection: 'column', pl: '1rem' }}
                >
                  <Typography variant='body1'>
                    {user.first_name} {user.last_name}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: theme => theme.palette.semiBlack.main }}
                  >
                    {user.email}
                  </Typography>
                </Box>
              </Box>

              <Box
                onClick={handleCloseTrigger}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  ':hover': {
                    cursor: 'pointer',
                    bgcolor: theme => theme.palette.grey[50],
                    borderRadius: '6px',
                  },
                  height: '30px',
                  width: '30px',
                }}
              >
                <ClearIcon />
              </Box>
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                gap: '1rem',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'left',
                pb: '1rem',
              }}
            >
              <Chip
                sx={{
                  backgroundColor: getStatusColor(user.status),
                  color: theme => theme.palette.common.white,
                }}
                label={StatusLabels[user.status] || 'Status unknown'}
              />

              <Typography
                variant='body2'
                sx={{ color: theme => theme.palette.semiBlack.main }}
              >
                {t('adminPanel.users.lastActive')}: {userLastActive}
              </Typography>
            </Box>

            {/* BEGIN: Action buttons */}

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                gap: '1rem',
              }}
            >
              <Button
                type='button'
                variant='outlined'
                color='primary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'primary.main',
                  width: '100%',
                }}
                onClick={handleResetPasswordOpen}
                loadingPosition='center'
                disabled={isResetPasswordDisabled}
              >
                {t('adminPanel.users.resetPassword.buttonLabel')}
              </Button>

              <Button
                type='button'
                variant='outlined'
                color='primary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'primary.main',
                  width: '100%',
                }}
                onClick={handleResendInvitationOpen}
                loadingPosition='center'
                disabled={isResendInviteDisabled}
              >
                {t('adminPanel.users.resendInvitation.buttonLabel')}
              </Button>

              {isUserDeactivated ? (
                <Button
                  type='button'
                  variant='outlined'
                  color='success'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                    color: 'success',
                    width: '100%',
                  }}
                  onClick={reactivateUser}
                  loadingPosition='center'
                  disabled={isDeactivateUserDisabled}
                >
                  {t('adminPanel.users.reactivate')}
                </Button>
              ) : (
                <Button
                  type='button'
                  variant='outlined'
                  color='error'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                    color: 'error',
                    width: '100%',
                  }}
                  onClick={handleDeactivateOpen}
                  loadingPosition='center'
                >
                  {t('adminPanel.users.confirmDeactivate.buttonLabel')}
                </Button>
              )}
            </Box>

            {/* END: Action buttons  */}

            {/* BEGIN: Confirmation modals */}

            <ConfirmationModal
              title={t('adminPanel.users.confirmDeactivate.title')}
              message={t('adminPanel.users.confirmDeactivate.description')}
              confirmText={t('adminPanel.users.confirmDeactivate.deactivate')}
              onConfirm={confirmDeactivate}
              onClose={handleConfirmDeactivateClose}
              isOpen={isConfirmDeactivateOpen}
              isLoading={isMutationPending}
            />

            <ConfirmationModal
              title={t('adminPanel.users.resendInvitation.title')}
              message={t('adminPanel.users.resendInvitation.description')}
              onConfirm={confirmResendInvitation}
              onClose={handleResendInvitationClose}
              isOpen={isResendInvitationOpen}
              isLoading={isMutationPending}
            />

            <ResetPasswordModal
              title={t('adminPanel.users.resetPassword.title')}
              message={t('adminPanel.users.resetPassword.description')}
              onConfirm={confirmResetPassword}
              onClose={handleResetPasswordClose}
              isOpen={isResetPasswordOpen}
            />

            <DeactivateUserModal
              isOpen={isDeactivateOpen}
              onClose={handleDeactivateClose}
              userId={user.id}
              action={handleConfirmDeactivateOpen}
            />

            {/* END: Confirmation modals */}

            <TabContext value={tabIndex}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <TabList onChange={handleTriggerChangeTab}>
                  <Tab label='GENERAL' value='1' />
                  <Tab label='LICENSES' value='2' />
                  <Tab label='HISTORY' value={historyTabIndex} />
                </TabList>
              </Box>
              <TabPanel
                sx={{
                  padding: '0 !important',
                }}
                value='1'
              >
                <Box
                  sx={{
                    maxHeight: 'calc(100vh - 250px)',
                    overflowY: 'auto',
                    pt: 1,
                  }}
                >
                  <General user={user} />
                </Box>
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                }}
                value='2'
              >
                <Box
                  sx={{
                    maxHeight: 'calc(100vh - 250px)',
                    overflowY: 'auto',
                    pt: 1,
                  }}
                >
                  <Licenses user={user} />
                </Box>
              </TabPanel>
              <TabPanel
                sx={{
                  padding: '0 !important',
                }}
                value={historyTabIndex}
              >
                <Box
                  sx={{
                    maxHeight: 'calc(100vh - 250px)',
                    overflowY: 'auto',
                    pt: 1,
                  }}
                >
                  {userHistory && (
                    <History
                      history={userHistory.history}
                      count={userHistory.count}
                      page={historyPage}
                      changePage={setHistoryPage}
                    />
                  )}
                </Box>
              </TabPanel>
            </TabContext>
          </>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size='6rem' />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
